import type { ContentSummary } from '@augment-toolkit/core';

/**
 * Turning a flat list of phases into the lifecycle's shape.
 *
 * **`GET /content/phases` is sorted by `number` across the whole lifecycle**,
 * not within a stage, so the stage grouping is not in the payload. The L1 grid
 * and the lifecycle rail both need it and derived it separately until this
 * existed.
 */

/** Phases of one stage, in authored order. */
export type StageGroup = {
  /** The stage as the API spells it — `Design`, not `DESIGN`. */
  stage: string;
  stageNumber: number;
  phases: ContentSummary[];
};

/**
 * `stageNumber` of the stage that is not a step in the flow. Delivery Leadership
 * runs alongside every other stage, so it sits outside the grid as one
 * full-width card.
 *
 * **Keyed on the number rather than the name**: a stage renamed in Confluence
 * keeps its position, where a check against `'Delivery'` would silently drop it
 * back into the grid.
 */
export const CROSS_CUTTING_STAGE_NUMBER = 0;

/**
 * Every stage that has at least one phase, in taxonomy order, each holding its
 * phases in `number` order. A stage with no phases does not appear at all — an
 * empty column is a content problem, not something a grid should draw.
 */
export const groupPhasesByStage = (phases: ContentSummary[]): StageGroup[] => {
  const byStage = new Map<string, ContentSummary[]>();

  for (const phase of phases) {
    const existing = byStage.get(phase.stage);

    if (existing === undefined) {
      byStage.set(phase.stage, [phase]);
    } else {
      existing.push(phase);
    }
  }

  return [...byStage.entries()]
    .map(([stage, stagePhases]) => ({
      stage,
      // Every record of a stage carries the same stageNumber, so the first is
      // the stage's. The array is non-empty by construction above.
      stageNumber: stagePhases[0]?.stageNumber ?? 0,
      phases: [...stagePhases].sort((a, b) => a.number - b.number),
    }))
    .sort((a, b) => a.stageNumber - b.stageNumber);
};

/**
 * The lifecycle split into the stages the grid draws and the phases that run
 * across all of them. One call rather than two predicates, because a phase in
 * neither half is a phase that silently does not render.
 */
export const splitCrossCutting = (
  phases: ContentSummary[],
): { stages: StageGroup[]; crossCutting: ContentSummary[] } => {
  const groups = groupPhasesByStage(phases);

  return {
    stages: groups.filter((group) => group.stageNumber !== CROSS_CUTTING_STAGE_NUMBER),
    crossCutting: groups
      .filter((group) => group.stageNumber === CROSS_CUTTING_STAGE_NUMBER)
      .flatMap((group) => group.phases),
  };
};
